import { useState, useEffect } from "react";
import { AuthLayout } from "../layouts/AuthLayout";
import { validateInvitationToken } from "../../api/auth";
import "./LoginPage.css";

type SetPasswordPageProps = {
  token: string;
  onSubmit: (
    password: string,
    confirmPassword: string,
    phone: string,
    address_line1?: string | null,
    address_line2?: string | null,
    city?: string | null,
    postal_code?: string | null,
    province?: string | null,
    country?: string | null
  ) => Promise<void>;
};

type TokenStatus = "loading" | "valid" | "invalid" | "expired" | "already_used";

export function SetPasswordPage({ token, onSubmit }: SetPasswordPageProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address_line1, setAddressLine1] = useState("");
  const [address_line2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postal_code, setPostalCode] = useState("");
  const [province, setProvince] = useState("QC");
  const [country, setCountry] = useState("CA");
  // Removed showOptionalFields - address fields are now always visible
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>("loading");
  const [userEmail, setUserEmail] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setTokenStatus("invalid");
      return;
    }

    // Validate token on mount
    validateInvitationToken(token)
      .then((data) => {
        setTokenStatus("valid");
        setUserEmail(data.email);
      })
      .catch((error: any) => {
        const code = error.code || error.message || "token_invalid";
        if (code === "token_expired") {
          setTokenStatus("expired");
        } else if (code === "token_already_used") {
          setTokenStatus("already_used");
        } else {
          setTokenStatus("invalid");
        }
      });
  }, [token]);

  if (!token || tokenStatus === "invalid") {
    return (
      <AuthLayout>
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFLOW</div>
            <h1>Lien d'invitation invalide</h1>
            <p style={{ marginBottom: "1rem" }}>
              Le lien d'invitation que vous avez utilisé n'est pas valide ou est incomplet.
            </p>
            <p style={{ fontSize: "0.9rem", color: "#666" }}>
              Veuillez vérifier le lien reçu par email ou demander une nouvelle invitation à votre administrateur.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (tokenStatus === "expired") {
    return (
      <AuthLayout>
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFLOW</div>
            <h1>Lien d'invitation expiré</h1>
            <p style={{ marginBottom: "1rem" }}>
              Désolé, ce lien d'invitation a expiré. Les liens d'invitation sont valides pour une durée limitée pour votre sécurité.
            </p>
            <p style={{ fontSize: "0.9rem", color: "#666" }}>
              Veuillez contacter votre administrateur pour recevoir une nouvelle invitation.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (tokenStatus === "already_used") {
    return (
      <AuthLayout>
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFLOW</div>
            <h1>Lien d'invitation déjà utilisé</h1>
            <p style={{ marginBottom: "1rem" }}>
              Ce lien d'invitation a déjà été utilisé pour activer votre compte.
            </p>
            <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "1.5rem" }}>
              Vous pouvez maintenant vous connecter avec votre mot de passe.
            </p>
            <button
              type="button"
              className="login-primary"
              onClick={() => {
                window.location.hash = "#/";
              }}
            >
              Aller à la page de connexion
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (tokenStatus === "loading") {
    return (
      <AuthLayout>
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFLOW</div>
            <h1>Vérification du lien...</h1>
            <p>Veuillez patienter pendant que nous validons votre lien d'invitation.</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="login-page">
        <div className="login-header">
          <div className="login-brand">XLFLOW</div>
          <h1>Activer votre compte</h1>
          {userEmail && (
            <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "0.5rem" }}>
              {userEmail}
            </p>
          )}
          <p>Complétez vos informations et définissez votre mot de passe pour finaliser l'activation.</p>
        </div>

        <form
          className="login-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setErrorMessage("");
            
            // Validation
            if (!phone.trim()) {
              setErrorMessage("Le numéro de téléphone est obligatoire.");
              return;
            }
            
            setIsSubmitting(true);
            try {
              await onSubmit(
                password,
                confirmPassword,
                phone,
                address_line1 || null,
                address_line2 || null,
                city || null,
                postal_code || null,
                province || null,
                country || null
              );
            } catch (error) {
              setErrorMessage((error as Error).message || "Échec de l'activation");
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {/* Informations obligatoires */}
          <label className="login-field">
            Téléphone *
            <input
              type="tel"
              placeholder="(xxx) xxx-xxxx"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              required
              autoComplete="tel"
            />
          </label>

          {/* Mot de passe */}
          <label className="login-field">
            Mot de passe *
            <input
              type="password"
              placeholder="8 caractères minimum"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="new-password"
              minLength={8}
            />
          </label>

          <label className="login-field">
            Confirmer le mot de passe *
            <input
              type="password"
              placeholder="Répétez votre mot de passe"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              autoComplete="new-password"
              minLength={8}
            />
          </label>

          {/* Informations d'adresse (toujours visibles, optionnelles) */}
          <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid #e5e7eb" }}>
            <h3 style={{ fontSize: "0.9rem", fontWeight: 600, color: "#374151", marginBottom: "1rem" }}>
              Informations d'adresse
            </h3>
            
            <label className="login-field">
              Adresse
              <input
                type="text"
                placeholder="123 rue Principale"
                value={address_line1}
                onChange={(event) => setAddressLine1(event.target.value)}
                autoComplete="street-address"
              />
            </label>

            <label className="login-field">
              Complément d'adresse
              <input
                type="text"
                placeholder="Appartement, bureau, etc."
                value={address_line2}
                onChange={(event) => setAddressLine2(event.target.value)}
                autoComplete="address-line2"
              />
            </label>

            {/* Responsive grid: 1 column on mobile, 2 columns on larger screens */}
            <div className="address-grid address-grid-city-postal">
              <label className="login-field">
                Ville
                <input
                  type="text"
                  placeholder="Montréal"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  autoComplete="address-level2"
                />
              </label>

              <label className="login-field">
                Code postal
                <input
                  type="text"
                  placeholder="H1A 1A1"
                  value={postal_code}
                  onChange={(event) => setPostalCode(event.target.value)}
                  autoComplete="postal-code"
                />
              </label>
            </div>

            {/* Responsive grid: 1 column on mobile, 2 columns on larger screens */}
            <div className="address-grid address-grid-province-country">
              <label className="login-field">
                Province
                <select
                  value={province}
                  onChange={(event) => setProvince(event.target.value)}
                  autoComplete="address-level1"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    fontSize: "1rem",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="QC">Québec</option>
                  <option value="ON">Ontario</option>
                  <option value="BC">Colombie-Britannique</option>
                  <option value="AB">Alberta</option>
                  <option value="MB">Manitoba</option>
                  <option value="SK">Saskatchewan</option>
                  <option value="NS">Nouvelle-Écosse</option>
                  <option value="NB">Nouveau-Brunswick</option>
                  <option value="NL">Terre-Neuve-et-Labrador</option>
                  <option value="PE">Île-du-Prince-Édouard</option>
                  <option value="NT">Territoires du Nord-Ouest</option>
                  <option value="YT">Yukon</option>
                  <option value="NU">Nunavut</option>
                </select>
              </label>

              <label className="login-field">
                Pays
                <select
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  autoComplete="country"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    fontSize: "1rem",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="CA">Canada</option>
                  <option value="US">États-Unis</option>
                </select>
              </label>
            </div>
          </div>

          {errorMessage ? <p className="text-xs text-danger" style={{ marginTop: "0.5rem" }}>{errorMessage}</p> : null}

          <button type="submit" className="login-primary" disabled={isSubmitting} style={{ marginTop: "1.5rem" }}>
            {isSubmitting ? "Activation..." : "Activer le compte"}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
